import "./CrossChannel.scss";
import { LOCATIONS } from "../NavBar/locations";
import { computeResolvedDate } from "@blisspointmedia/bpm-types/dist/RelativeDatePicker";
import { dataInfoExcelExport, getDataInfo } from "../utils/download-utils";
import { DatePicker, Dropdown, FullPageSpinner, Page } from "../Components";
import { DateRange, StateSetter } from "../utils/types";
import { FetchFilterBar, useFilterPaneState } from "../SingleChannel/FetchFilterBar/FetchFilterBar";
import { FilterPaneState } from "@blisspointmedia/bpm-types/dist/FilterPane";
import { FilterPreset } from "@blisspointmedia/bpm-types/dist/pgTables/FilterPreset";
import { getChannelSeriesColor, shouldUsePrimaryPalette } from "../utils/colors";
import { Metric, KpiInfo } from "@blisspointmedia/bpm-types/dist/CrossChannel";
import { Mixpanel, MxE } from "../utils/mixpanelWrapper";
import { navigate, RouteComponentProps } from "@reach/router";
import { TEST_COMPANIES } from "@blisspointmedia/bpm-types/dist/TestCompanies";
import { useCompanyInfo } from "../redux/company";
import { useSelector } from "react-redux";
import { useSetError } from "../redux/modals";
import * as CC from "@blisspointmedia/bpm-types/dist/CrossChannelPerformance";
import * as R from "ramda";
import * as UserRedux from "../redux/user";
import DeliveryAndPerformance from "./Tabs/DeliveryAndPerformance";
import EditPagePresetModal from "../SingleChannel/MetricsTableConfig/EditPagePresetModal";
import Metrics from "./Tabs/MetricsTableView";
import Overview from "./Tabs/Overview";
import PresetDropdown from "../SingleChannel/PresetDropdown";
import React, { useState, useMemo, useEffect, useCallback } from "react";
import RenameModal from "../SingleChannel/RenameModal";
import SourcePicker, { SourceType } from "./Components/SourcePicker";
import useLocation from "../utils/hooks/useLocation";
import {
  awaitJSON,
  CrossChannelLambdaFetch,
  SingleChannelLambdaFetch,
  ToolsLambdaFetch,
} from "../utils/fetch-utils";
import {
  dimensionColumnMetaDataMap,
  getCrossChannelDimensionCell,
  calculateOverviewMetricsByDay,
  makeColumnMetaData,
  getCrossChannelColumnMetaData,
} from "./crossChannelUtils";
import {
  DateInfo,
  DeletePresetParams as DeletePagePresetParams,
  GetPresetsParams as GetPagePresetsParams,
  GlobalOptions,
  MetricsPagePreset,
  SavePresetParams as SavePagePresetParams,
  SavePresetResponse as SavePagePresetResponse,
} from "@blisspointmedia/bpm-types/dist/MetricsPage";
import {
  CROSS_CHANNEL,
  DEFAULT_DATE_RANGE_STATE,
  DEFAULT_OTHER_DATE_RANGE_STATE,
  GetPresetsParams as GetTablePresetsParams,
  PresetIDGroups as SimpleMetricsTablePreset,
} from "@blisspointmedia/bpm-types/dist/MetricsTable";
import {
  computeDateFromPreset,
  computeDateFromComparisonPreset,
  computeCustomRelativeRange,
} from "@blisspointmedia/bpm-types/dist/RelativeDatePresets";
import {
  ColumnMetaData,
  convertDBCustomMetric,
} from "../SingleChannel/MetricsTable/metricsTableUtils";
import {
  decodePrettyUrl,
  encodePrettyUrl,
  getBaseURL,
  STANDARD_GROUP_NAME,
} from "../Performance/performanceUtils";
import {
  GetCustomMetricsParams,
  GetCustomMetricsResponse,
} from "@blisspointmedia/bpm-types/dist/CustomMetrics";
import TotalBusiness from "./Tabs/TotalBusiness";
import {
  CompanyFiscalCal,
  OUTSIDE_RANGE_ERR_MSG,
} from "@blisspointmedia/bpm-types/dist/FiscalCalendar";
import { useExperimentFlag } from "../utils/experiments/experiment-utils";

export const enum TabKey {
  OVERVIEW = "overview",
  DELIVERY_AND_PERFORMANCE = "delivery",
  METRICS_TABLE = "metrics-table",
  TOTAL_BUSINESS = "total-business",
}

const NAVS = [
  { label: "Overview", key: TabKey.OVERVIEW },
  { label: "Delivery & Performance", key: TabKey.DELIVERY_AND_PERFORMANCE },
  { label: "Metrics Table", key: TabKey.METRICS_TABLE },
  { label: "Total Business", key: TabKey.TOTAL_BUSINESS },
];

interface CrossChannelContextType {
  company: string;
  dates: DateRange;
  otherDates: DateRange;
  selectedChannels: Record<string, boolean>;
  deliveryAndPerformanceChannels: Record<string, boolean>;
  channelColors: Record<string, string>;
  kpi: string;
  crossChannelKPIs: string[];
  platformAsSourceOfData: boolean;
  selectedSources: string[];
  // URL
  setURL: (pagePresetName: string, tablePresetName?: string) => void;
  // Page Preset
  pagePreset?: MetricsPagePreset;
  // Filter Preset
  filterData?: CC.GetFilterOptionsResponse;
  filterID?: number;
  filterSource?: "presetFilterID" | "currentFilterID" | "presetState" | "currentState";
  filterState?: FilterPaneState;
  setFilterData: StateSetter<CC.GetFilterOptionsResponse | undefined>;
  // Table Preset
  fetchingTablePresets: boolean;
  getTablePresets: () => void;
  setFetchingTablePresets: StateSetter<boolean>;
  tablePresetName?: string;
  tablePresets?: SimpleMetricsTablePreset[];
  // Metrics Table
  metricsTableDataMap: Record<string, any>;
  setMetricsTableDataMap: StateSetter<Record<string, any>>;
  metricsTableDataKey: string;
  metricsTableDataOtherKey: string;
  customSegments: Record<string, boolean>;
  dataInfo: Record<string, string>[];
  fiscalCal: CompanyFiscalCal;
}

export const CrossChannelContext = React.createContext<CrossChannelContextType>({
  company: "",
  dates: { start: "", end: "" },
  otherDates: { start: "", end: "" },
  selectedChannels: {},
  deliveryAndPerformanceChannels: {},
  channelColors: {},
  kpi: "",
  crossChannelKPIs: [],
  platformAsSourceOfData: false,
  selectedSources: [],
  // Metrics Table
  metricsTableDataMap: {},
  setMetricsTableDataMap: () => {},
  metricsTableDataKey: "",
  metricsTableDataOtherKey: "",
  // Table Preset
  fetchingTablePresets: false,
  getTablePresets: () => {},
  setFetchingTablePresets: () => {},
  // Filters
  setFilterData: () => {},
  // URL
  setURL: () => {},
  customSegments: {},
  dataInfo: [],
  fiscalCal: {},
});

interface CrossChannelProps extends RouteComponentProps {
  urlSettings: string;
}

const CrossChannel: React.FC<CrossChannelProps> = ({ location, urlSettings }) => {
  // Check if we're on the experimental branch
  const isExperimental =
    process.env.REACT_APP_DOCKER_LAMBDA_HOST === "https://api-staging-2.blisspointmedia.com" ||
    window.location.hostname === "localhost";
  const { company } = useLocation();
  const companyInfo = useCompanyInfo();
  const { defaultCrossChannelSource } = companyInfo;
  const { fiscal_cal: fiscalCal } = companyInfo;
  const pagePresetURLString = "_page-preset=";
  const prefix = CROSS_CHANNEL;
  const setError = useSetError();
  const tablePresetURLString = "_table-preset=";
  const isAdmin = useSelector(UserRedux.isAdminSelector);

  // State Variables
  const [budgetsDataMap, setBudgetsDataMap] = useState<Record<string, any>>({});
  const [channelClassification, setChannelClassification] = useState<Record<string, string>>();
  const [customColumnMetaDataMap, setCustomColumnMetaDataMap] = useState<
    Record<string, ColumnMetaData>
  >();
  const [customSegments, setCustomSegments] = useState<Record<string, any>>({});
  const [dates, setDates] = useState<DateRange | undefined>();
  const [fetchingPagePresets, setFetchingPagePresets] = useState(false);
  const [fetchingTablePresets, setFetchingTablePresets] = useState(false);
  const [filterData, setFilterData] = useState<CC.GetFilterOptionsResponse>();
  const [filterID, setFilterID] = useState<number | undefined>();
  const [filterPresets, setFilterPresets] = useState<FilterPreset[]>();
  const [filterSource, setFilterSource] = useState<
    "presetFilterID" | "currentFilterID" | "presetState" | "currentState"
  >();
  const [filterState, setFilterState, resetFilterState] = useFilterPaneState();
  const [kpi, setKpi] = useState<string | undefined>();
  const [kpiDataMap, setKpiDataMap] = useState<Record<string, KpiInfo>>({});
  const [sourceOptions, setSourceOptions] = useState<{ label: string; value: string }[]>([]);
  const [selectedSources, setSelectedSources] = useState<string[]>(
    defaultCrossChannelSource ? [defaultCrossChannelSource] : []
  );
  const [totalBusinessSource, setTotalBusinessSource] = useState<string | undefined>(
    defaultCrossChannelSource ? defaultCrossChannelSource : undefined
  );
  const [marketingInputs, setMarketingInputs] = useState<any[]>();
  const [metricsDataMap, setMetricsDataMap] = useState<Record<string, any>>({});
  const [metricsTableDataMap, setMetricsTableDataMap] = useState<Record<string, any>>({});
  const [metricsTableFetchFilterBar, setMetricsTableFetchFilterBar] = useState<JSX.Element>();
  const [otherDates, setOtherDates] = useState<DateRange | undefined>();
  const [pagePresetChanges, setPagePresetChanges] = useState<MetricsPagePreset>({
    globalOptions: {},
  } as MetricsPagePreset);
  const [pagePresetName, setPagePresetName] = useState<string>();
  const [pagePresets, setPagePresets] = useState<MetricsPagePreset[]>();
  const [selectedChannels, setSelectedChannels] = useState<Record<string, boolean>>({});
  const [selectedTab, setSelectedTab] = useState<string>();
  const [showEditPagePresetModal, setShowEditPagePresetModal] = useState(false);
  const [showNewPagePresetModal, setShowNewPagePresetModal] = useState(false);
  const [showRenamePagePresetModal, setShowRenamePagePresetModal] = useState(false);
  const [tablePresetName, setTablePresetName] = useState<string>();
  const [tablePresets, setTablePresets] = useState<SimpleMetricsTablePreset[]>();
  const [metricsDataLoading, setMetricsDataLoading] = useState<boolean>(false);
  const [deliveryAndPerformanceChannels, setDeliveryAndPerformanceChannels] = useState<
    Record<string, boolean>
  >({});
  const columnMetaDataMap = useMemo(() => getCrossChannelColumnMetaData(company), [company]);
  const showCCDataMarketingInputs = useExperimentFlag("showCCDataMarketingInputs");

  const platformAsSourceOfData = useMemo(
    () => selectedSources.length === 1 && selectedSources.includes("platform"),
    [selectedSources]
  );

  const baseUrl = useMemo(() => {
    return getBaseURL(urlSettings, location);
  }, [urlSettings, location]);

  const budgetsDataKey = useMemo(() => {
    if (!dates || !otherDates || !filterState) {
      return "";
    }
    return `${company}_${dates.start}_${dates.end}_${otherDates.start}_${
      otherDates.end
    }_${JSON.stringify(filterState)}`;
  }, [company, dates, otherDates, filterState]);

  const kpiDataKey = useMemo(
    () => (dates ? `${company}_${dates.start}_${dates.end}_${totalBusinessSource}` : ""),
    [company, dates, totalBusinessSource]
  );

  const metricsTableDataKey = useMemo(
    () =>
      dates
        ? `${tablePresetName}_${filterID}_${JSON.stringify(filterState)}_${kpi}_${dates.start}_${
            dates.end
          }_${selectedSources.join("")}`
        : "",
    [dates, filterID, filterState, kpi, tablePresetName, selectedSources]
  );

  const metricsTableDataOtherKey = useMemo(
    () =>
      otherDates
        ? `${tablePresetName}_${filterID}_${JSON.stringify(filterState)}_${kpi}_${
            otherDates.start
          }_${otherDates.end}_${platformAsSourceOfData ? "platform" : "analytics"}`
        : "",
    [otherDates, filterID, filterState, kpi, tablePresetName, platformAsSourceOfData]
  );

  const [startingDateRange, startingOtherDateRange] = useMemo(() => {
    const localDateRangeString = localStorage.getItem(`${company}__crossChannelDateRange`);
    const localDateRange = localDateRangeString ? JSON.parse(localDateRangeString) : null;
    const localOtherDateRangeString = localStorage.getItem(`${company}__crossChannelDateRange`);
    const localOtherDateRange = localOtherDateRangeString
      ? JSON.parse(localOtherDateRangeString)
      : null;
    return [
      {
        start:
          localDateRange && localDateRange.start
            ? localDateRange.start
            : computeResolvedDate(DEFAULT_DATE_RANGE_STATE.start),
        end:
          localDateRange && localDateRange.end
            ? localDateRange.end
            : computeResolvedDate(DEFAULT_DATE_RANGE_STATE.end),
      },
      {
        start:
          localOtherDateRange && localOtherDateRange.start
            ? localOtherDateRange.start
            : computeResolvedDate(DEFAULT_OTHER_DATE_RANGE_STATE.start),
        end:
          localOtherDateRange && localOtherDateRange.end
            ? localOtherDateRange.end
            : computeResolvedDate(DEFAULT_OTHER_DATE_RANGE_STATE.end),
      },
    ];
  }, [company]);

  const kpiData = useMemo(() => kpiDataMap[kpiDataKey], [kpiDataMap, kpiDataKey]);
  const crossChannelKPIs = useMemo(() => {
    const kpiMap: Record<string, boolean> = {};
    if (
      isExperimental &&
      companyInfo &&
      companyInfo.cid === "tinuititest" &&
      kpiData &&
      kpiData.kpiData
    ) {
      for (const row of kpiData.kpiData) {
        kpiMap[row.kpi] = true;
      }
      return R.keys(kpiMap);
    }
    return R.defaultTo([], companyInfo.cross_channel_kpis);
  }, [companyInfo, isExperimental, kpiData]);

  const budgetsData = useMemo(() => R.pathOr([], [budgetsDataKey], budgetsDataMap), [
    budgetsDataMap,
    budgetsDataKey,
  ]);

  const channelColors = useMemo(() => {
    const channels = R.keys(selectedChannels).sort();
    const usePrimaryPalette = shouldUsePrimaryPalette(channels.length);
    return channels.reduce((prev, curr, i) => {
      return {
        ...prev,
        [curr]: getChannelSeriesColor(
          i,
          usePrimaryPalette,
          TEST_COMPANIES.includes(company) || company.includes("tinuitidemo")
        ),
      };
    }, {});
  }, [company, selectedChannels]);

  // URL Callback
  const setURL = useCallback(
    (pagePresetName: string, tablePresetName?: string, selectedTabInput?: string) => {
      // To avoid infinite renders, only update the URL and page preset if it's different from
      // the current state
      const newURL = `${location?.origin}${baseUrl}/${R.defaultTo(
        selectedTab,
        selectedTabInput
      )}${pagePresetURLString}${encodePrettyUrl(pagePresetName)}${
        tablePresetName ? `${tablePresetURLString}${encodePrettyUrl(tablePresetName)}` : ""
      }`
        .replace("/crosschannel", "/cross-channel")
        .replace("/cross_channel", "/cross-channel");
      const isLocationDifferent = location && location.href !== newURL;
      if (selectedTab && isLocationDifferent) {
        setPagePresetName(pagePresetName);
        setTablePresetName(tablePresetName);
        navigate(newURL);
      }
    },
    [location, baseUrl, selectedTab]
  );

  // Dates Callbacks
  const onDatePickerChange = useCallback(
    (dates: DateRange) => {
      localStorage.setItem(`${company}_crossChannelDateRange`, JSON.stringify(dates));
      setDates(dates);
    },
    [company, setDates]
  );

  const onOtherDatePickerChange = useCallback(
    (otherDates: DateRange) => {
      localStorage.setItem(`${company}_crossChannelDateRange`, JSON.stringify(otherDates));
      setOtherDates(otherDates);
    },
    [company, setOtherDates]
  );

  // Page Preset
  const pagePreset = useMemo(() => {
    if (
      pagePresets &&
      tablePresets &&
      !fetchingPagePresets &&
      !fetchingTablePresets &&
      sourceOptions
    ) {
      const foundPagePreset = R.find(
        (preset: MetricsPagePreset) => preset.name === pagePresetName,
        pagePresets
      );
      if (foundPagePreset) {
        const pagePreset = R.clone(foundPagePreset);
        const pagePresetChanges = R.clone(foundPagePreset);
        if (pagePreset.dateInfo) {
          const {
            start,
            end,
            dateType,
            relative_date_info,
            comparison_start,
            comparison_end,
            comparison_dateType,
            comparison_relative_date_info,
          } = pagePreset.dateInfo;
          let resolvedPrimaryDates: DateRange | undefined;
          let resolvedComparisonDates: DateRange | undefined;
          // Set Primary Dates
          if (dateType === "relative" && relative_date_info && relative_date_info !== "Custom") {
            try {
              if (typeof relative_date_info === "string") {
                const newRange = computeDateFromPreset(relative_date_info, fiscalCal);
                resolvedPrimaryDates = newRange;
              } else {
                const newRange = computeCustomRelativeRange(
                  relative_date_info.start,
                  relative_date_info.end,
                  fiscalCal
                );
                resolvedPrimaryDates = newRange;
              }
            } catch {
              console.error(OUTSIDE_RANGE_ERR_MSG);
            }
          } else if (start && end) {
            resolvedPrimaryDates = {
              start: start,
              end: end,
            };
          }
          if (resolvedPrimaryDates) {
            pagePreset.dateInfo = pagePreset.dateInfo = {
              ...R.defaultTo({}, pagePreset.dateInfo),
              ...resolvedPrimaryDates,
            } as DateInfo;
          } else {
            setDates(startingDateRange);
            pagePreset.dateInfo = pagePreset.dateInfo = {
              ...R.defaultTo({}, pagePreset.dateInfo),
              ...startingDateRange,
            } as DateInfo;
          }
          if (R.isNil(dates)) {
            setDates(pagePreset.dateInfo);
            pagePresetChanges.dateInfo = pagePreset.dateInfo;
          } else {
            pagePresetChanges.dateInfo = pagePresetChanges.dateInfo = {
              ...R.defaultTo({}, pagePresetChanges.dateInfo),
              ...dates,
            } as DateInfo;
          }
          /// Set Comparison Dates
          if (
            comparison_dateType === "relative" &&
            comparison_relative_date_info &&
            comparison_relative_date_info !== "Custom" &&
            resolvedPrimaryDates
          ) {
            if (typeof comparison_relative_date_info === "string") {
              try {
                const newRange = computeDateFromComparisonPreset(
                  comparison_relative_date_info,
                  resolvedPrimaryDates.start,
                  resolvedPrimaryDates.end,
                  fiscalCal
                );
                resolvedComparisonDates = newRange;
              } catch {
                console.error(OUTSIDE_RANGE_ERR_MSG);
              }
            } else {
              try {
                const newRange = computeCustomRelativeRange(
                  comparison_relative_date_info.start,
                  comparison_relative_date_info.end,
                  fiscalCal
                );
                resolvedComparisonDates = newRange;
              } catch {
                console.error(OUTSIDE_RANGE_ERR_MSG);
              }
            }
          } else if (comparison_start && comparison_end) {
            resolvedComparisonDates = {
              start: comparison_start,
              end: comparison_end,
            };
          }
          if (!resolvedComparisonDates) {
            resolvedComparisonDates = startingOtherDateRange;
          }
          pagePreset.dateInfo = {
            ...pagePreset.dateInfo,
            comparison_start: resolvedComparisonDates.start,
            comparison_end: resolvedComparisonDates.end,
          } as DateInfo;
          if (R.isNil(otherDates)) {
            setOtherDates({
              start: pagePreset.dateInfo.comparison_start as string,
              end: pagePreset.dateInfo.comparison_end as string,
            });
            pagePresetChanges.dateInfo = pagePreset.dateInfo;
          } else {
            pagePresetChanges.dateInfo = pagePresetChanges.dateInfo = {
              ...R.defaultTo({}, pagePresetChanges.dateInfo),
              comparison_start: otherDates.start,
              comparison_end: otherDates.end,
            } as DateInfo;
          }
        } else {
          if (R.isNil(dates)) {
            setDates(startingDateRange);
            pagePresetChanges.dateInfo = {
              ...R.defaultTo({}, pagePresetChanges.dateInfo),
              ...startingDateRange,
            } as DateInfo;
          }
          if (R.isNil(otherDates)) {
            setOtherDates(startingOtherDateRange);
            pagePresetChanges.dateInfo = {
              ...R.defaultTo({}, pagePresetChanges.dateInfo),
              comparison_start: startingOtherDateRange.start,
              comparison_end: startingOtherDateRange.end,
            } as DateInfo;
          }
        }
        // Set Filter Info
        if (pagePreset.filterInfo && R.isNil(filterSource)) {
          if (!R.isNil(pagePreset.filterInfo.filterID)) {
            setFilterSource("presetFilterID");
            setFilterID(pagePreset.filterInfo.filterID);
          } else if (pagePreset.filterInfo.filterState) {
            setFilterSource("presetState");
            setFilterState(pagePreset.filterInfo.filterState);
          } else {
            setFilterSource("currentState");
            resetFilterState();
          }
        } else {
          pagePresetChanges.filterInfo = {};
          if (filterID) {
            pagePresetChanges.filterInfo.filterID = filterID;
          } else if (filterState) {
            pagePresetChanges.filterInfo.filterState = filterState;
          }
        }
        // Set Table Preset
        if (R.isNil(tablePresetName) && tablePresets && pagePreset.globalOptions) {
          const pageSourcedTableID = pagePreset.globalOptions.tableID;
          const pageSourcedTablePreset = R.find(
            preset => preset.id === pageSourcedTableID,
            tablePresets
          );
          if (pageSourcedTablePreset) {
            setURL(pagePreset.name, pageSourcedTablePreset.name);
          } else if (tablePresets && tablePresets.length) {
            for (const tablePreset of tablePresets) {
              if (!tablePreset.temporary) {
                pagePreset.globalOptions.tableID = tablePreset.id;
                (pagePresetChanges.globalOptions as GlobalOptions).tableID = tablePreset.id;
                setURL(pagePreset.name, tablePreset.name);
                break;
              }
            }
          }
        } else if (!R.isNil(tablePresetName)) {
          const pageSourcedTablePreset = R.find(
            preset => preset.name === tablePresetName,
            tablePresets
          );
          if (pageSourcedTablePreset) {
            (pagePresetChanges.globalOptions as GlobalOptions).tableID = pageSourcedTablePreset.id;
          }
        }
        if (!kpi && pagePreset.globalOptions && pagePreset.globalOptions.kpi) {
          const newKPI = pagePreset.globalOptions.kpi || "";
          setKpi(newKPI);
          pagePresetChanges.globalOptions.kpi = newKPI;
        } else if (!kpi && crossChannelKPIs) {
          const newKPI = crossChannelKPIs[0] || "";
          setKpi(newKPI);
          pagePresetChanges.globalOptions.kpi = newKPI;
        } else if (kpi) {
          pagePresetChanges.globalOptions.kpi = kpi;
        }
        if (R.isEmpty(selectedSources)) {
          if (pagePreset.globalOptions && pagePreset.globalOptions.source) {
            const source = pagePreset.globalOptions.source || "";
            setSelectedSources([source]);
            setTotalBusinessSource(source);
            pagePresetChanges.globalOptions.source = source;
          } else if (defaultCrossChannelSource) {
            const source = defaultCrossChannelSource || "";
            setSelectedSources([source]);
            setTotalBusinessSource(source);
            pagePresetChanges.globalOptions.source = source;
          } else if (sourceOptions && sourceOptions[0] && sourceOptions[0].value) {
            const source = sourceOptions[0].value;
            setSelectedSources([source]);
            setTotalBusinessSource(source);
            pagePresetChanges.globalOptions.source = source;
          }
        } else {
          pagePresetChanges.globalOptions.source = selectedSources;
        }
        setPagePresetChanges(pagePresetChanges);
        setPagePresetName(p => {
          if (p !== null && p !== pagePreset.name) {
            Mixpanel.track(MxE.PAGE_PRESET_CHANGE, {
              option_selected: pagePreset.name,
            });
          }
          return pagePreset.name;
        });
        return pagePreset;
      }
    }
  }, [
    crossChannelKPIs,
    dates,
    defaultCrossChannelSource,
    fetchingPagePresets,
    fetchingTablePresets,
    filterID,
    filterSource,
    filterState,
    kpi,
    otherDates,
    pagePresetName,
    pagePresets,
    resetFilterState,
    selectedSources,
    setFilterState,
    setURL,
    sourceOptions,
    startingDateRange,
    startingOtherDateRange,
    tablePresetName,
    tablePresets,
    fiscalCal,
  ]);

  const metricsDataKey = useMemo(
    () =>
      dates && otherDates
        ? `filtered_${filterID}_${JSON.stringify(filterState)}_${kpi}_${dates.start}_${dates.end}_${
            otherDates.start
          }_${otherDates.end}_${selectedSources.join("")}`
        : "",
    [kpi, dates, otherDates, selectedSources, filterID, filterState]
  );

  const metricsData = useMemo(() => metricsDataMap[metricsDataKey], [
    metricsDataMap,
    metricsDataKey,
  ]);

  const [metricTotalsByDate, otherMetricTotalsByDate] = useMemo(() => {
    return [
      calculateOverviewMetricsByDay({
        metricsData: R.pathOr([], ["primaryMetrics"], metricsData),
        kpiData: R.pathOr([], ["kpiData"], kpiData),
        selectedKpi: kpi,
      }),
      calculateOverviewMetricsByDay({
        metricsData: R.pathOr([], ["otherMetrics"], metricsData),
        kpiData: R.pathOr([], ["kpiData"], kpiData),
        selectedKpi: kpi,
      }),
    ];
  }, [kpiData, metricsData, kpi]);

  // Page Preset Callbacks
  const getPagePresets = useCallback(() => {
    (async () => {
      try {
        setFetchingPagePresets(true);
        const res = await SingleChannelLambdaFetch<GetPagePresetsParams>("/metrics_page_presets", {
          params: {
            company,
            mediatype: prefix,
          },
        });
        const parsedPresets = await awaitJSON(res);
        if (!parsedPresets.errorMessage) {
          setPagePresets(parsedPresets as MetricsPagePreset[]);
        }
      } catch (e) {
        const error = e as Error;
        setError({
          message: `Failed to fetch page presets. Error: ${error.message}`,
          reportError: error,
        });
      } finally {
        setFetchingPagePresets(false);
      }
    })();
  }, [company, prefix, setError]);

  const savePagePreset = useCallback(
    body => {
      return (async () => {
        let presetResponse;
        try {
          setFetchingPagePresets(true);
          const res = await SingleChannelLambdaFetch<SavePagePresetParams>("/metrics_page_preset", {
            method: "POST",
            body,
          });
          presetResponse = await awaitJSON<SavePagePresetResponse>(res);
          return presetResponse;
        } catch (e) {
          const error = e as Error;
          setError({
            message: `Failed to save page preset. Error: ${error.message}`,
            reportError: error,
          });
          presetResponse = { message: `Failed to save page preset. Error: ${error.message}` };
        } finally {
          getPagePresets();
        }
      })();
    },
    [getPagePresets, setError]
  );

  const deletePagePreset = useCallback(
    params => {
      return (async () => {
        try {
          setFetchingPagePresets(true);
          const res = await SingleChannelLambdaFetch<DeletePagePresetParams>(
            "/metrics_page_preset",
            {
              method: "DELETE",
              params,
            }
          );
          await awaitJSON(res);
        } catch (e) {
          const error = e as Error;
          setError({
            message: `Failed to delete page preset. Error: ${error.message}`,
            reportError: error,
          });
        } finally {
          getPagePresets();
        }
      })();
    },
    [getPagePresets, setError]
  );

  // Table Preset Callbacks
  const getTablePresets = useCallback(() => {
    (async () => {
      try {
        setFetchingTablePresets(true);
        setTablePresets(undefined);
        const res = await SingleChannelLambdaFetch<GetTablePresetsParams>(
          "/metrics_table_presets",
          {
            params: {
              company,
              mediatype: prefix,
            },
          }
        );
        const parsedPresets = await awaitJSON(res);
        if (!parsedPresets.errorMessage) {
          setTablePresets(parsedPresets as SimpleMetricsTablePreset[]);
        }
      } catch (e) {
        const error = e as Error;
        setError({
          message: `Failed to fetch table presets. Error: ${error.message}`,
          reportError: error,
        });
      } finally {
        setFetchingTablePresets(false);
      }
    })();
  }, [company, prefix, setError, setTablePresets]);

  // useEffects
  useEffect(() => {
    if (
      (baseUrl.includes("/crosschannel") || baseUrl.includes("/cross_channel")) &&
      pagePresetName
    ) {
      setURL(pagePresetName, tablePresetName, selectedTab);
    }
  }, [baseUrl, pagePresetName, selectedTab, tablePresetName, setURL]);

  useEffect(() => {
    if (metricsDataLoading) {
      return;
    }
    (async () => {
      try {
        if (
          !metricsDataMap[metricsDataKey] &&
          dates &&
          otherDates &&
          (kpi || kpi === "") &&
          otherDates
        ) {
          const overviewParams: any = {
            company,
            start: dates.start,
            end: dates.end,
            otherStart: otherDates.start,
            otherEnd: otherDates.end,
            pageID: 0,
            kpi: kpi,
            lag: companyInfo.streaming_performance_default_lag,
            sources: selectedSources.join(","),
            channel: prefix,
          };
          const paramsWithFilters: any = { ...overviewParams };

          if (filterSource === "currentState" && !R.isNil(filterState)) {
            paramsWithFilters.filterState = JSON.stringify(filterState);
          } else if (filterSource === "currentFilterID" && !R.isNil(filterID)) {
            paramsWithFilters.filterID = filterID;
          } else if (
            pagePreset &&
            filterSource === "presetFilterID" &&
            !R.isNil(pagePreset.filterInfo.filterID)
          ) {
            paramsWithFilters.filterID = pagePreset.filterInfo.filterID;
          } else if (
            pagePreset &&
            filterSource === "presetState" &&
            pagePreset.filterInfo.filterState
          ) {
            paramsWithFilters.filterState = JSON.stringify(pagePreset.filterInfo.filterState);
          }

          if (!metricsDataMap[metricsDataKey]) {
            setMetricsDataLoading(true);
            const res = await CrossChannelLambdaFetch("/getCrossChannelMetrics", {
              params: paramsWithFilters,
            });
            const data = await awaitJSON(res);
            setMetricsDataMap(current => ({
              ...current,
              [metricsDataKey]: data,
            }));
            const availableChannels = data.channels.reduce(
              (prev, curr) => ({ ...prev, [curr]: true }),
              {}
            );
            setDeliveryAndPerformanceChannels(availableChannels);
            setSelectedChannels(c => ({ ...c, ...availableChannels }));
            setMetricsDataLoading(false);
          }
        }
      } catch (e) {
        const reportError = e as Error;
        setMetricsDataLoading(false);
        setError({
          message: `Failed to get cross channel metrics. ERROR: ${reportError.message}`,
          reportError,
        });
      }
    })();
  }, [
    kpi,
    company,
    dates,
    otherDates,
    companyInfo.streaming_performance_default_lag,
    metricsDataMap,
    setError,
    selectedSources,
    prefix,
    customSegments,
    filterID,
    filterState,
    filterSource,
    pagePreset,
    metricsDataKey,
    metricsDataLoading,
  ]);

  useEffect(() => {
    (async () => {
      try {
        if (R.isEmpty(sourceOptions)) {
          const res = await CrossChannelLambdaFetch("/getSourceOfTruthOptions", {
            params: {
              company,
              ...LOCATIONS["Cross-Channel"].subPages.Overview.testCompanyParams,
            },
          });

          const data = await awaitJSON(res);

          setSourceOptions(data || []);
        }
      } catch (e) {
        const reportError = e as Error;
        setError({
          message: `Failed to get cross channel Source of Truth options. ERROR: ${reportError.message}`,
          reportError,
        });
      }
    })();
  }, [company, sourceOptions, setError]);

  useEffect(() => {
    (async () => {
      try {
        if (!kpiDataMap[kpiDataKey] && dates && totalBusinessSource) {
          const res = await CrossChannelLambdaFetch("/getCrossChannelKpis", {
            params: {
              company,
              start: dates.start,
              end: dates.end,
              source: totalBusinessSource,
              ...LOCATIONS["Cross-Channel"].subPages.Overview.testCompanyParams,
            },
          });
          const data = await awaitJSON(res);

          // TODO: delete this?
          const availableChannels = data.kpiData.reduce((acc, { channel }) => {
            if (channel === "") {
              return acc;
            }
            return { ...acc, [channel]: true };
          }, {});
          setKpiDataMap(current => ({ ...current, [kpiDataKey]: data }));
          setSelectedChannels(c => ({ ...c, ...availableChannels }));
        }
      } catch (e) {
        const reportError = e as Error;
        setError({
          message: `Failed to get cross channel KPIs. ERROR: ${reportError.message}`,
          reportError,
        });
      }
    })();
  }, [kpi, company, dates, kpiDataMap, kpiDataKey, totalBusinessSource, setError]);

  useEffect(() => {
    (async () => {
      try {
        if (!budgetsDataMap[budgetsDataKey] && dates && otherDates) {
          const params: any = {
            company,
            start: dates.start,
            end: dates.end,
            otherStart: otherDates.start,
            otherEnd: otherDates.end,
            ...LOCATIONS["Cross-Channel"].subPages.Overview.testCompanyParams,
          };

          if (!R.isNil(filterState)) {
            params.filterState = JSON.stringify(filterState);
          }

          const res = await CrossChannelLambdaFetch("/getCrossChannelBudgets", {
            params,
          });
          const data = await awaitJSON(res);
          setBudgetsDataMap(current => ({ ...current, [budgetsDataKey]: data }));
        }
      } catch (e) {
        const reportError = e as Error;
        setError({
          message: `Failed to get cross channel budgets. ERROR: ${reportError.message}`,
          reportError,
        });
      }
    })();
  }, [budgetsDataKey, budgetsDataMap, company, dates, filterState, otherDates, setError]);

  useEffect(() => {
    (async () => {
      if (dates) {
        try {
          const res = await ToolsLambdaFetch("/getMarketingPortfolio", {
            params: {
              company,
              start: dates.start,
              end: dates.end,
              kpi: companyInfo.initial_kpi,
              ...LOCATIONS["Cross-Channel"].subPages.Overview.testCompanyParams,
            },
          });
          const { channelSpend, channelClassification } = await awaitJSON(res);
          setMarketingInputs(channelSpend);
          setChannelClassification(channelClassification);
        } catch (e) {
          const reportError = e as Error;
          setError({
            message: `Failed to get marketing inputs. ERROR: ${reportError.message}`,
            reportError,
          });
        }
      }
    })();
  }, [company, dates, companyInfo.initial_kpi, setError]);

  useEffect(() => {
    if (company && !filterData) {
      (async () => {
        try {
          const res = await CrossChannelLambdaFetch<
            CC.GetFilterOptionsParams & { channel: string; pageID: string }
          >("/getCrossChannelFilterOptions", {
            params: {
              company: company,
              ...LOCATIONS["Cross-Channel"].subPages.Overview.testCompanyParams,
            },
          });
          const filterOptions = await awaitJSON<CC.GetFilterOptionsResponse>(res);

          // Get the custom segments from the filter options
          const customSegments = filterOptions
            .filter(option => option.isCustomSegment)
            .reduce(
              (curr, prev) => ({
                ...curr,
                [prev.label]: makeColumnMetaData(prev.label as any),
              }),
              {}
            );
          setFilterData(
            R.filter(
              option => !["Campaign ID", "Campaign Name"].includes(option.label),
              R.defaultTo([], filterOptions)
            )
          );
          setCustomSegments(customSegments);
        } catch (e) {
          const error = e as Error;
          setError({
            message: error.message,
            reportError: error,
          });
        }
      })();
    }
  }, [company, filterData, prefix, setError, setFilterData]);

  // Combine the default cross-channel dimensions with the company-specific custom segments
  const allDimensions = useMemo(() => ({ ...dimensionColumnMetaDataMap, ...customSegments }), [
    customSegments,
  ]);

  useEffect(() => {
    if (company && !pagePresets) {
      getPagePresets();
    }
  }, [company, getPagePresets, pagePresets, setError]);

  useEffect(() => {
    if (company && !tablePresets) {
      getTablePresets();
    }
  }, [company, getTablePresets, setError, tablePresets]);

  useEffect(() => {
    if (R.isNil(selectedTab)) {
      if (urlSettings.startsWith(TabKey.DELIVERY_AND_PERFORMANCE)) {
        setSelectedTab(TabKey.DELIVERY_AND_PERFORMANCE);
      } else if (urlSettings.startsWith(TabKey.METRICS_TABLE)) {
        setSelectedTab(TabKey.METRICS_TABLE);
      } else if (urlSettings.startsWith(TabKey.OVERVIEW)) {
        setSelectedTab(TabKey.OVERVIEW);
      } else if (urlSettings.startsWith(TabKey.TOTAL_BUSINESS)) {
        setSelectedTab(TabKey.TOTAL_BUSINESS);
      } else {
        setSelectedTab(TabKey.OVERVIEW);
      }
    }
  }, [selectedTab, urlSettings]);

  useEffect(() => {
    if (
      selectedTab &&
      pagePresets &&
      pagePresets.length &&
      tablePresets &&
      tablePresets.length &&
      !fetchingPagePresets &&
      !fetchingTablePresets &&
      !pagePresetName // We want to only use this logic when we load up the page
    ) {
      const decodedURLPagePresetName = decodePrettyUrl(
        urlSettings.substring(
          selectedTab.length + pagePresetURLString.length,
          urlSettings.indexOf(tablePresetURLString)
        )
      );
      const decodedURLTablePresetName = decodePrettyUrl(
        urlSettings.substring(
          urlSettings.indexOf(tablePresetURLString) + tablePresetURLString.length,
          urlSettings.length
        )
      );
      if (R.isNil(tablePresetName) && decodedURLTablePresetName) {
        setTablePresetName(decodedURLTablePresetName);
      }
      if (R.isNil(pagePresetName) && decodedURLPagePresetName) {
        setPagePresetName(decodedURLPagePresetName);
      }
      const urlPagePreset = R.find(preset => preset.name === decodedURLPagePresetName, pagePresets);
      const urlTablePreset = R.find(
        preset => preset.name === decodedURLTablePresetName,
        tablePresets
      );
      if (
        R.isNil(urlPagePreset) ||
        R.isNil(urlTablePreset) ||
        (pagePreset && pagePreset.name !== urlPagePreset.name)
      ) {
        if (urlPagePreset) {
          setURL(urlPagePreset.name, urlTablePreset?.name);
        } else if (pagePresets && pagePresets.length) {
          for (const preset of pagePresets) {
            if (!preset.temporary) {
              setURL(preset.name, urlTablePreset?.name);
              setPagePresetName(preset.name);
              break;
            }
          }
        }
      }
    }
  }, [
    fetchingPagePresets,
    fetchingTablePresets,
    pagePreset,
    pagePresetName,
    pagePresets,
    selectedTab,
    setURL,
    tablePresetName,
    tablePresets,
    urlSettings,
  ]);

  const getCustomMetrics = useCallback(() => {
    (async () => {
      const customMetricsResp = await SingleChannelLambdaFetch<GetCustomMetricsParams>(
        "/getCustomMetrics",
        {
          params: {
            company,
            channel: prefix,
          },
        }
      );
      const customMetrics = await awaitJSON<GetCustomMetricsResponse>(customMetricsResp);
      const customColumnMetaDataMap: Record<string, ColumnMetaData> = {};
      for (const metric of customMetrics) {
        const convertedMetric = convertDBCustomMetric(metric);
        if (convertedMetric) {
          customColumnMetaDataMap[metric.name] = convertedMetric;
        }
      }
      setCustomColumnMetaDataMap(customColumnMetaDataMap);
    })();
  }, [company, prefix]);

  useEffect(() => {
    (async () => {
      if (R.isNil(customColumnMetaDataMap)) {
        await getCustomMetrics();
      }
    })();
  }, [getCustomMetrics, customColumnMetaDataMap]);

  const kpiNameMap = useMemo(() => {
    const kpiNameMap = {};
    for (const kpi of crossChannelKPIs) {
      kpiNameMap[kpi] = {
        cpxName: `CP${kpi.slice(0).toUpperCase()}`,
        decimals: 0,
        id: kpi,
        kpi,
        name: kpi,
        revenueHeader: null,
      };
    }
    return kpiNameMap;
  }, [crossChannelKPIs]);

  const pagePresetGroups = useMemo(() => {
    const groupsMap = {};
    if (pagePresets) {
      for (const preset of pagePresets) {
        if (preset.presetGroup && preset.presetGroup !== STANDARD_GROUP_NAME) {
          groupsMap[preset.presetGroup] = true;
        }
      }
    }
    return R.keys(groupsMap).sort();
  }, [pagePresets]);

  // Page Preset Modals
  const editPagePresetModal = (showEditPagePresetModal || showNewPagePresetModal) &&
    kpiData &&
    pagePresetChanges && (
      <EditPagePresetModal
        mediatype={"cross-channel"}
        menuTabs={[
          "Dates",
          "KPI, etc.",
          "Cross-Channel Overview",
          "Delivery & Performance",
          "Metrics Table",
          "Filters",
          "Preset Group",
          "Total Business",
        ]}
        filterPresets={filterPresets}
        funnelPresets={kpiData.funnelPresets}
        pagePresetChanges={pagePresetChanges}
        globalOptionsPickers={{
          kpi: {
            options: R.map(
              kpi => ({ label: kpi, value: kpi }),
              R.defaultTo([], crossChannelKPIs) as string[]
            ),
          },
          source: sourceOptions,
        }}
        groups={pagePresetGroups}
        sparkChartOptions={[
          Metric.CLICKS_CONVERSION_RATE,
          Metric.IMPS_CONVERSION_RATE,
          Metric.CPM,
          Metric.CPX,
          Metric.CTR,
          Metric.IMPRESSIONS,
          Metric.REVENUE,
          Metric.ROAS,
          Metric.SPEND,
          Metric.VOLUME,
          Metric.CLICKS,
        ]}
        setPagePresetChanges={setPagePresetChanges}
        saveAndApplyOnClick={presetChanges => {
          (async () => {
            if (
              !R.isNil(presetChanges) &&
              !R.isNil(presetChanges.filterInfo) &&
              !R.isNil(presetChanges.filterInfo.filterState)
            ) {
              presetChanges.filterInfo.filterState = filterState;
            }
            const decodedURLTablePresetName = decodePrettyUrl(
              urlSettings.substring(
                urlSettings.indexOf(tablePresetURLString) + tablePresetURLString.length,
                urlSettings.length
              )
            );
            const presetResponse = await savePagePreset({
              company: company,
              id: R.defaultTo("new", presetChanges.id),
              mediatype: prefix,
              name: presetChanges.name,
              group: presetChanges.presetGroup,
              preset: presetChanges,
              temporary: presetChanges.temporary,
            });
            if (
              presetChanges.temporary ||
              (presetChanges.id as any) === "new" ||
              presetChanges.id <= 0 ||
              (pagePreset && presetChanges.id === pagePreset.id)
            ) {
              setSelectedSources([]);
              setKpi(undefined);
              setDates(undefined);
              setOtherDates(undefined);
              setFilterID(undefined);
              setURL(presetResponse.name, decodedURLTablePresetName);
            }
            setShowEditPagePresetModal(false);
            setShowNewPagePresetModal(false);
          })();
        }}
        tablePresets={R.filter(
          tablePreset => !tablePreset.temporary,
          R.defaultTo([], tablePresets)
        )}
        onHide={() => {
          setShowEditPagePresetModal(false);
          setShowNewPagePresetModal(false);
          if (pagePreset) {
            setPagePresetChanges(pagePreset);
          }
        }}
      />
    );

  const renamePagePresetModal = showRenamePagePresetModal && pagePresetChanges && (
    <RenameModal
      applyOnClick={newName => {
        (async () => {
          const decodedURLTablePresetName = decodePrettyUrl(
            urlSettings.substring(
              urlSettings.indexOf(tablePresetURLString) + tablePresetURLString.length,
              urlSettings.length
            )
          );
          await savePagePreset({
            company: company,
            id: pagePresetChanges.id,
            mediatype: prefix,
            name: newName,
            group: pagePresetChanges.presetGroup,
          });
          setShowRenamePagePresetModal(false);
          setURL(newName, decodedURLTablePresetName);
        })();
      }}
      applyText={"Save"}
      cancelText={"Discard"}
      disabled={pagePresetChanges.globalOptions.admin_lock && isAdmin}
      onCancel={() => {
        setShowRenamePagePresetModal(false);
        if (pagePreset) {
          setPagePresetChanges(pagePreset);
        }
      }}
      onHide={() => {
        setShowRenamePagePresetModal(false);
        if (pagePreset) {
          setPagePresetChanges(pagePreset);
        }
      }}
      placeholder="Enter new preset name"
      subTitle={"New Page Preset Name"}
      title={`Rename ${pagePresetChanges && pagePresetChanges.name ? pagePresetChanges.name : ""}`}
    />
  );

  /*
    If a client has marketing inputs from TVAD, use that. Otherwise, use cross channel spend
    data to power the Marketing Inputs chart so that it's not blank.
  */
  const [totalBusinessMarketingInputs, totalBusinessChannelClassification] = useMemo(() => {
    if (R.isEmpty(marketingInputs) || showCCDataMarketingInputs) {
      let channelSpendByDate: Record<string, any> = {};
      let channels: Record<string, string> = {};
      const data = R.pathOr([], ["primaryMetrics"], metricsData);
      for (let item of data) {
        const { date, channel, metrics } = item;
        const { spend } = metrics;
        channels[channel] = "Spend";

        if (!channelSpendByDate[date]) {
          channelSpendByDate[date] = {};
        }

        channelSpendByDate[date] = R.mergeDeepRight(channelSpendByDate[date], {
          [channel]: R.pathOr(0, [date, channel], channelSpendByDate) + spend,
        });
      }

      const inputs = Object.entries(channelSpendByDate)
        .map(([date, channelSpend]) => ({
          date,
          ...channelSpend,
        }))
        .sort((a, b) => a.date.localeCompare(b.date));

      return [inputs, channels];
    } else {
      let channels: Record<string, string> = { ...channelClassification };
      for (let chan of R.keys(marketingInputs ? marketingInputs[0] || {} : {})) {
        if (chan !== "date" && chan !== "Interpolated" && !channels[chan as string]) {
          channels[chan as string] = "Spend";
        }
      }
      return [marketingInputs, channels];
    }
  }, [marketingInputs, channelClassification, metricsData, showCCDataMarketingInputs]);

  // Tab Render Item
  const renderItem: JSX.Element | undefined = useMemo(() => {
    if (
      !fetchingPagePresets &&
      pagePresetChanges &&
      !R.isNil(pagePresetChanges.id) &&
      customColumnMetaDataMap
    ) {
      const overview = (
        <Overview
          data={metricTotalsByDate}
          otherData={otherMetricTotalsByDate}
          defaultCompareMetrics={pagePresetChanges?.globalOptions?.compareMetrics}
          defaultCompareMetricsIncludeOtherDates={
            pagePresetChanges?.globalOptions?.compareMetricsIncludeOtherDates
          }
          defaultSparkCharts={pagePresetChanges?.globalOptions?.sparkCharts}
        />
      );
      switch (selectedTab) {
        case TabKey.OVERVIEW:
          return overview;
        case TabKey.DELIVERY_AND_PERFORMANCE:
          return (
            <DeliveryAndPerformance
              // getCrossChannelMetrics
              data={R.pathOr([], ["primaryMetrics"], metricsData)}
              otherData={R.pathOr([], ["otherMetrics"], metricsData)}
              // getCrossChannelBudgets
              budgetsData={budgetsData}
              defaultBudgetSnapshotBottom={pagePresetChanges?.globalOptions?.sbSnapshotBottom}
              defaultBudgetSnapshotTop={pagePresetChanges?.globalOptions?.sbSnapshotTop}
              defaultDeliveryIncludeOtherDates={
                pagePresetChanges?.globalOptions?.crossChannelDeliveryIncludeOtherDates
              }
              defaultDeliveryMetric={pagePresetChanges?.globalOptions?.crossChannelDeliveryMetric}
              defaultPerformanceBreakdownIncludeOtherDates={
                pagePresetChanges?.globalOptions?.crossChannelPerformanceIncludeOtherDates
              }
              defaultPerformanceBreakdownMetric={
                pagePresetChanges?.globalOptions?.crossChannelPerformanceMetric
              }
              defaultPerformanceSnapshotMetric={
                pagePresetChanges?.globalOptions?.crossChannelPerformanceSnapshotMetric
              }
              metricsDataLoading={metricsDataLoading}
            />
          );

        case TabKey.METRICS_TABLE:
          return (
            <Metrics
              columnMetaDataMap={{ ...columnMetaDataMap, ...customColumnMetaDataMap }}
              dates={R.defaultTo(startingDateRange, dates)}
              dimensionColumnMetaDataMap={allDimensions}
              getDimensionCell={getCrossChannelDimensionCell}
              kpi={R.defaultTo("", kpi)}
              kpiMetaData={kpiNameMap}
              metricsTableFetchFilterBar={metricsTableFetchFilterBar}
              prefix={"cross_channel"}
              refreshCustomMetrics={getCustomMetrics}
              setMetricsTableFetchFilterBar={setMetricsTableFetchFilterBar}
            />
          );
        case TabKey.TOTAL_BUSINESS:
          return (
            <TotalBusiness
              kpiInfo={kpiData}
              deliveryData={metricTotalsByDate}
              marketingInputs={totalBusinessMarketingInputs}
              channelClassification={totalBusinessChannelClassification}
              defaultFunnelDynamicsPreset={pagePresetChanges?.globalOptions?.funnelDynamicsName}
            />
          );
        default:
          return overview;
      }
    }
  }, [
    allDimensions,
    budgetsData,
    customColumnMetaDataMap,
    dates,
    fetchingPagePresets,
    getCustomMetrics,
    kpi,
    kpiData,
    kpiNameMap,
    metricsData,
    metricsDataLoading,
    metricsTableFetchFilterBar,
    metricTotalsByDate,
    otherMetricTotalsByDate,
    pagePresetChanges,
    selectedTab,
    startingDateRange,
    columnMetaDataMap,
    totalBusinessMarketingInputs,
    totalBusinessChannelClassification,
  ]);

  const {
    selectedSourceType,
    selectedSourceValues,
    selectedSourceValuePrettyName,
  } = useMemo(() => {
    if (platformAsSourceOfData) {
      return {
        selectedSourceType: SourceType.PLATFORM,
        selectedSourceValues: { platform: true },
        selectedSourceValuePrettyName: SourceType.PLATFORM,
      };
    } else {
      const sourceValues = selectedSources.reduce((prev, curr) => ({ ...prev, [curr]: true }), {});
      return {
        selectedSourceType: SourceType.SOURCE_OF_TRUTH,
        selectedSourceValues: sourceValues,
        selectedSourceValuePrettyName: sourceOptions.reduce(
          (prev, curr) => ({ ...prev, [curr.value]: curr.label }),
          {}
        ),
      };
    }
  }, [selectedSources, sourceOptions, platformAsSourceOfData]);

  const {
    selectedTotalBusinessSourceType,
    selectedTotalBusinessSourceValue,
    selectedTotalBusinessSourceValuePrettyName,
  } = useMemo(() => {
    if (totalBusinessSource === "platform") {
      return {
        selectedTotalBusinessSourceType: SourceType.PLATFORM,
        selectedTotalBusinessSourceValue: { platform: true },
        selectedTotalBusinessSourceValuePrettyName: SourceType.PLATFORM,
      };
    } else {
      return {
        selectedTotalBusinessSourceType: SourceType.SOURCE_OF_TRUTH,
        selectedTotalBusinessSourceValue: { [totalBusinessSource || ""]: true },
        selectedTotalBusinessSourceValuePrettyName: sourceOptions.reduce(
          (prev, curr) => ({ ...prev, [curr.value]: curr.label }),
          {}
        ),
      };
    }
  }, [totalBusinessSource, sourceOptions]);

  // Used for Excel exports
  const dataInfo = useMemo(() => {
    if (!filterData || !filterState) {
      return [];
    }

    const sources = selectedSources
      .map(source => (sourceOptions.find(option => option.value === source) || {}).label || source)
      .join(", ");

    const data = getDataInfo({
      kpi: kpi || "",
      sources,
      filterData,
      filterState,
    });

    const exportData = dataInfoExcelExport(data);

    return exportData;
  }, [filterData, filterState, kpi, selectedSources, sourceOptions]);

  return (
    <Page
      app2Redesign
      pageType="Cross-Channel"
      title="Cross-Channel"
      navs={NAVS}
      selectedNav={selectedTab}
      onNav={nav => {
        setSelectedTab(nav);
        if (pagePresetName) {
          setURL(pagePresetName, tablePresetName, nav);
        }
      }}
      actions={
        <div className="ccPageActions">
          <PresetDropdown
            addPresetOnClick={() => {
              setShowNewPagePresetModal(true);
              // In the case that we are in the standard group already, clear this out
              setPagePresetChanges(
                preset =>
                  ({
                    ...preset,
                    id: "new",
                    name: "New Page Preset!",
                    presetGroup: "",
                    dateInfo: {
                      ...preset.dateInfo,
                      dateType: "relative",
                      comparison_dateType: "relative",
                      relative_date_info: "Last Month",
                      comparison_relative_date_info: "Default",
                    },
                  } as any)
              );
            }}
            addPresetText={"Add New Page Preset"}
            applyOnClick={preset => {
              setURL(preset.name);
              setKpi(undefined);
              setDates(undefined);
              setOtherDates(undefined);
              setFilterSource(undefined);
              setSelectedSources([]);
            }}
            cancelOnClick={() => {}}
            className={`pagePresetPicker ${fetchingPagePresets ? "loading" : ""}`}
            defaultPresetNames={["Default"]}
            deleteItemOnClick={presetID => deletePagePreset({ id: presetID, mediatype: prefix })}
            duplicateItemOnClick={preset =>
              savePagePreset({
                company,
                id: "new",
                mediatype: prefix,
                preset: preset,
                name: `${preset.name} (Copy)`,
                group: preset.presetGroup,
              })
            }
            editItemOnClick={preset => {
              const presetCopy = { ...preset };
              if (
                preset.dateInfo &&
                preset.dateInfo.dateType === "relative" &&
                !preset.dateInfo.relative_date_info
              ) {
                presetCopy.dateInfo.dateType = "exact";
              }
              if (preset.dateInfo && !preset.dateInfo.comparison_relative_date_info) {
                presetCopy.dateInfo.comparison_dateType = "exact";
              }
              setPagePresetChanges(presetCopy);
              setShowEditPagePresetModal(true);
            }}
            hasPresetGroups={true}
            hasTicks={true}
            label={
              pagePreset && !R.isNil(pagePreset.id)
                ? `Page Preset: ${pagePreset.temporary ? "Temp" : pagePreset.name}`
                : "Load Page Preset"
            }
            presets={R.filter(
              preset => !preset.temporary || (!R.isNil(pagePreset) && preset.id === pagePreset.id),
              R.defaultTo([], pagePresets)
            )}
            renameItemOnClick={preset => {
              setPagePresetChanges(preset);
              setShowRenamePagePresetModal(true);
            }}
            selectedPreset={pagePreset}
            subTitles={["Preset Group", "Preset"]}
          />
          {!R.isEmpty(selectedSources) && selectedTab !== TabKey.TOTAL_BUSINESS && (
            <SourcePicker
              selectedSourceType={selectedSourceType}
              selectedSourceValue={selectedSourceValues}
              sourceValueOptions={sourceOptions}
              applyOnClick={sources => {
                setSelectedSources(sources);
              }}
              platforms={R.defaultTo([], metricsData?.platforms)}
              label={`Source: ${selectedSourceValuePrettyName}`}
            />
          )}
          {selectedTab === TabKey.TOTAL_BUSINESS && (
            <SourcePicker
              selectedSourceType={selectedTotalBusinessSourceType}
              selectedSourceValue={selectedTotalBusinessSourceValue}
              sourceValueOptions={sourceOptions}
              applyOnClick={sources => {
                setTotalBusinessSource(sources[0]);
              }}
              platforms={R.defaultTo([], metricsData?.platforms)}
              label={`Total Business Source: ${selectedTotalBusinessSourceValuePrettyName}`}
              singleSelect
            />
          )}
          <Dropdown
            background="dark"
            dropdownMenuClassName="ccKpiPicker"
            label="KPI"
            value={R.defaultTo("", kpi)}
            options={crossChannelKPIs}
            onChange={option => {
              Mixpanel.track(MxE.PAGE_KPI_CHANGE, {
                option_selected: option,
              });
              setKpi(option);
            }}
          />
          <DatePicker
            range={dates}
            onChange={({ start, end }) => {
              onDatePickerChange({ start, end });
            }}
            comparison={true}
            comparisonRange={otherDates}
            comparisonOnChange={({ start, end }) => {
              onOtherDatePickerChange({ start, end });
            }}
            datePresets={true}
          />
        </div>
      }
      subHeader={
        selectedTab !== TabKey.TOTAL_BUSINESS && (
          <div className="ccPageSubHeader">
            {filterData && (
              <FetchFilterBar
                categories={filterData}
                company={company}
                filterID={filterID}
                filters={filterPresets}
                filterSource={filterSource}
                platform={prefix}
                refreshData={() => {
                  setMetricsDataMap(map => ({ ...map, [metricsDataKey]: undefined }));
                  setMetricsTableDataMap(map => ({ ...map, [metricsTableDataKey]: undefined }));
                }}
                setFilterID={setFilterID}
                setFilters={setFilterPresets}
                setFilterSource={
                  setFilterSource as StateSetter<
                    "presetFilterID" | "currentFilterID" | "presetState" | "currentState"
                  >
                }
                setState={setFilterState}
                state={filterState}
              />
            )}
          </div>
        )
      }
    >
      {metricsData &&
      kpiData &&
      metricTotalsByDate &&
      otherMetricTotalsByDate &&
      !fetchingPagePresets ? (
        <CrossChannelContext.Provider
          value={{
            company,
            dates: R.defaultTo(startingDateRange, dates),
            otherDates: R.defaultTo(startingOtherDateRange, otherDates),
            selectedChannels,
            deliveryAndPerformanceChannels,
            channelColors,
            kpi: R.defaultTo("", kpi),
            crossChannelKPIs,
            platformAsSourceOfData,
            selectedSources,
            // URL
            setURL,
            // Page Preset
            pagePreset,
            // Filter Presets
            filterData,
            filterID,
            filterSource,
            filterState,
            setFilterData,
            // Table Preset
            fetchingTablePresets,
            getTablePresets,
            setFetchingTablePresets,
            tablePresetName,
            tablePresets,
            // Metrics Table
            metricsTableDataMap,
            setMetricsTableDataMap,
            metricsTableDataKey,
            metricsTableDataOtherKey,
            customSegments: R.keys(customSegments).reduce(
              (prev, curr) => ({ ...prev, [curr]: true }),
              {}
            ),
            dataInfo,
            fiscalCal: fiscalCal,
          }}
        >
          {editPagePresetModal}
          {renamePagePresetModal}
          <div className="ccPage">{renderItem}</div>
        </CrossChannelContext.Provider>
      ) : (
        <FullPageSpinner />
      )}
    </Page>
  );
};

export default CrossChannel;
